
import { Popover } from "element-ui"
import debounce from "lodash/debounce"
import throttle from "lodash/throttle"
import breadcrumb from "../library/breadcrumb.vue"
import proRec from "../components/proRec.vue"
import {
  transformCateDataToCascaderData,
  priceFormat,
  priceFormatOther,
  getCartCheckedCount,
  openNoAddressPop,
  noAddressCheck,
  getStockMax,
  appendUrlParams
} from "../utils/utils"
import inputNumber from "../components/cms/inputNumber.vue"
import picZoom from "../components/picZoom/picZoom.vue"
import collectOrder from "../components/drawer/collectOrder.vue"
import { getProductDetail } from "../api/gateway"
import getCartNumMixin from "@/mixins/getCartNum"
import collapseTransition from "@/library/collapse-transition.js"
import tooltip from "@/components/tooltip.vue"
import commonConfig from "@/utils/common"
import { trackViewItem, trackAddToCart, handleChangeCartNumber } from "@/utils/track"
import { getHeadLink, getHeadMeta } from "@/utils/head"
import { handleAddToCartV2, handleModifyCartV2, handleGetCartDetailV2 } from "@/utils/cartResponse"
import { getRequestHeaders } from "@/utils/utils"
import { getTenantId } from "@/plugins/env"
import { Logger } from "@/plugins/logger"
import { omnichatViewProduct, omnichatAddToCart } from "@/utils/omnichat"
/**
 * 获取URL参数
 */
const getURLParams = ({ query, params }) => {
  const { sku, skuId } = Object.assign(query, params)
  return {
    skuId: sku || skuId
  }
}

export default {
  components: {
    [Popover.name]: Popover,
    breadcrumb,
    collapseTransition,
    inputNumber,
    proRec,
    picZoom,
    collectOrder,
    tooltip
  },
  mixins: [getCartNumMixin],
  validate({ query, params }) {
    return query.skuId || params.sku
  },
  async asyncData({ route, req, error, store }) {
    try {
      const urlParams = getURLParams(route)
      const { data } = await getProductDetail(
        {
          param: urlParams,
          comm: {}
        },
        {
          loading: true,
          headers: getRequestHeaders({ req, store })
        }
      )
      const cascaderData = transformCateDataToCascaderData(data.categoryList)
      const baseInfo = data.baseInfo
      baseInfo.showSoldOut = [1, 2].includes(baseInfo.wareStatus)
      const extendInfo = data.extendInfo
      const thresholdInfo = data.thresholdInfo || {}
      const defaultImg = baseInfo?.wareImgListNew?.[0]?.url
      const configData = data.wareRec
      const amount = baseInfo.promotionWareVO
        ? priceFormat(baseInfo.promotionWareVO.unitProPrice)[0] +
          "." +
          priceFormat(baseInfo.promotionWareVO.unitProPrice)[1]
        : baseInfo.warePrice
          ? priceFormat(baseInfo.warePrice)[0] + "." + priceFormat(baseInfo.warePrice)[1]
          : ""
      const lang = store.state.lang
      const fullPath = route.fullPath
      return {
        cascaderData,
        baseInfo,
        extendInfo,
        thresholdInfo,
        defaultImg,
        configData,
        skuId: urlParams.skuId,
        amount,
        lang: lang == "en_US" ? "en" : "zh-hant",
        fullPath
      }
    } catch (err) {
      Logger.error("商详初始化报错", err)
      error({
        statusCode: 500,
        message: "Internal Server Error"
      })
    }
  },
  data() {
    return {
      cascaderData: [],
      breadcrumbData: [],
      extendInfo: {},
      thresholdInfo: {},
      defaultImg: "",
      configData: [],
      baseInfo: {},
      scroll: null,
      showDes: true,
      freshnessTitle: "",
      freshnessInfo: "",
      addCartCount: 0,
      commonReqParams: {},
      cartList: [],
      collectOrderCartList: [],
      collectOrderItem: {},
      skuId: "",
      stock: "",
      amount: "",
      lang: "",
      fullPath: "",
      isFixed: false
    }
  },
  head() {
    return {
      title: `${this.baseInfo.wareName}`,
      meta: [
        { hid: "description", name: "description", content: `${this.baseInfo.wareName}` },
        { name: "og:type", content: "product" },
        { name: "og:title", content: `${this.baseInfo.wareName}` },
        { name: "og:description", content: `${this.baseInfo.wareName}` },
        { name: "og:image", content: this.defaultImg },
        { name: "product:price:amount", content: this.amount },
        { name: "product:price:currency", content: "HKD" },
        ...getHeadMeta(this)
      ],
      link: getHeadLink(this),
      script: [
        {
          hid: "ldjson-schema",
          type: "application/ld+json",
          innerHTML: `
          {
            "@context": "http://schema.org",
            "@type": "Product",
            "name": "${this.baseInfo.wareName}",
            "description": "${this.baseInfo.wareName}",
            "image": "${this.defaultImg}",
            "brand": {
              "@type": "Brand",
              "name": "${this.baseInfo.brandName || ""}"
            },
            "offers": {
              "@type": "Offer",
              "price": "${this.amount}",
              "priceCurrency": "HKD"
            }
          }
        `
        }
      ]
    }
  },
  created() {
    if (!process.client) {
      return
    }

    this.freshnessTitle = this.$t("productDetails.freshnessTitle")
    this.freshnessInfo = this.$t("productDetails.freshnessInfo", {
      num: this.baseInfo.freshness
    })
    this.initBreadcrumb()

    const { commonReqParams } = this.$commonParams
    this.commonReqParams = commonReqParams

    trackViewItem(this.baseInfo)
  },
  mounted() {
    this.$bus.$off("add-cart-success")
    this.$bus.$on("add-cart-success", () => {
      this.$nextTick(async () => {
        const loading = this.$loading({
          background: "rgba(0, 0, 0, 0)"
        })
        await handleGetCartDetailV2()
        loading.close()
      })
    })
    this.$bus.$off("getCartSkuList", this.init)
    // 组装当前商品已加购数据 和 推荐商品已加购数据
    this.$bus.$on("getCartSkuList", this.init)
    this.$nextTick(() => {
      this.handleScroll()
      window.addEventListener("scroll", this.handleScroll)
    })
    omnichatViewProduct({
      id: this.baseInfo.rfId,
      name: this.baseInfo.wareName,
      brand: this.baseInfo.brandName,
      category: this.baseInfo.categoryName,
      variant: "",
      price: this.baseInfo.promotionWareVO.unitProPrice / 100
    })
  },
  beforeDestroy() {
    window.removeEventListener("scroll", this.handleScroll)
  },
  methods: {
    priceFormat,
    priceFormatOther,
    getStockMax,
    handleScroll: throttle(function () {
      const actionsElement = this.$refs.actions
      if (!actionsElement) return
      const rect = actionsElement.getBoundingClientRect()
      this.isFixed = window.innerHeight - rect.height * 1.2 < rect.bottom
    }, 100),
    init(val) {
      this.cartList = val
      const selWares = {}
      const curSku = this.baseInfo.sku
      this.cartList.forEach(item => {
        selWares[item.sku] = { count: item.count, stock: item.stock }
      })
      const target = this.cartList.find(item => item.sku == curSku)
      this.addCartCount = target ? target.count : 0
      this.stock = target ? target.stock : 0
      this.configData.forEach(ware => {
        if (selWares[ware?.additional?.sku]) {
          this.$set(ware, "addCartCount", selWares[ware.additional.sku].count)
          this.$set(ware, "stock", selWares[ware.additional.sku].stock)
        } else {
          this.$set(ware, "addCartCount", 0)
        }
      })
    },
    initBreadcrumb() {
      const whereFrom = {
        name: this.$t("common.home"),
        path: this.MAIN_ORIGIN_HOME
      }
      const selPath = [{ name: this.baseInfo.wareName }]
      this.breadcrumbData = [whereFrom, ...selPath]
    },

    titleClick() {
      this.showDes = !this.showDes
    },

    async handleAddClick(item) {
      if (noAddressCheck()) {
        openNoAddressPop()
        return
      }

      const [error, data, wareList] = await handleAddToCartV2({
        // TODO storeId
        storeId: this.commonReqParams.storeId,
        wares: [{ sku: item.sku, beforeCount: 0, count: 1, checked: 1 }],
        sort: 1
      })
      if (data) {
        this.addCartCount = 1
        this.cartList = wareList
        const curWare = wareList.find(ware => ware.sku === item.sku) || {}
        this.stock = curWare.stock
        // omnichat fun
        omnichatAddToCart([
          {
            // id: this.baseInfo.rfId,
            id: this.baseInfo.sku || this.baseInfo.skuId,
            name: this.baseInfo.wareName,
            brand: this.baseInfo.brandName,
            category: this.baseInfo.categoryName,
            variant: "",
            quantity: curWare.count,
            price: this.baseInfo.promotionWareVO.unitProPrice / 100
          }
        ])
      } else {
        this.addCartCount = 0
        console.log("加购失败")
        if (error && error.code == "44999") {
          openNoAddressPop()
        }
      }
      trackAddToCart(this.baseInfo)
    },
    changeSkuCard: throttle(async function (item) {
      const { skuId, title } = item
      const newUrl = `${this.MAIN_ORIGIN}${this.MAIN_PATH}/p/${title}/i/${skuId}.html`
      window.history.replaceState(null, "", newUrl)
      this.fullPath = newUrl
      this.skuId = skuId
      // TODO: 接口高频率请求，返回值会出现data: {}
      const { data } = await getProductDetail(
        {
          param: { skuId },
          comm: {}
        },
        {
          loading: true
        }
      )
      this.cascaderData = transformCateDataToCascaderData(data.categoryList)
      this.baseInfo = data.baseInfo
      this.baseInfo.showSoldOut = [1, 2].includes(this.baseInfo.wareStatus)
      const { stock, count } = this.cartList.find(item => item.skuId === skuId) || { stock: 0, count: 0 }
      this.stock = stock
      this.addCartCount = count
      this.extendInfo = data.extendInfo
      this.thresholdInfo = data.thresholdInfo || {}
      this.defaultImg = this.baseInfo?.wareImgListNew?.[0]?.url + "_360x360H"
      this.configData = data.wareRec
      this.amount = this.baseInfo.promotionWareVO
        ? priceFormat(this.baseInfo.promotionWareVO.unitProPrice)[0] +
          "." +
          priceFormat(this.baseInfo.promotionWareVO.unitProPrice)[1]
        : this.baseInfo.warePrice
          ? priceFormat(this.baseInfo.warePrice)[0] + "." + priceFormat(this.baseInfo.warePrice)[1]
          : ""
      this.freshnessInfo = this.$t("productDetails.freshnessInfo", {
        num: this.baseInfo.freshness
      })
      // TODO: 确认是否触发埋点
      trackViewItem(this.baseInfo)
      omnichatViewProduct({
        id: this.baseInfo.rfId,
        name: this.baseInfo.wareName,
        brand: this.baseInfo.brandName,
        category: this.baseInfo.categoryName,
        variant: "",
        price: this.baseInfo.promotionWareVO.unitProPrice / 100
      })
    }, 500),
    breadcrumbClick(item) {
      return item.params ? `${this.MAIN_PATH}/category/${item.params.split(",").join("-")}/1.html` : `${this.MAIN_PATH}`
    },
    isNotSingleAndOrder(item) {
      return item.proType !== "UNIT" && item.proType !== "BASKET"
    },
    handleOfferClick(item) {
      if (!this.isNotSingleAndOrder(item)) {
        return
      }
      this.collectOrderCartList = this.cartList.map(item => ({
        sku: item.skuId,
        skuId: item.skuId,
        count: item.count,
        stock: item.stock
      }))

      this.handleOpenCollect(item)
    },
    handleOpenCollect(val) {
      const bestOffer = val.isBestoffer
      const params = {
        bestOffer
      }
      params.promotionId = val.proId
      params.skuId = this.skuId
      try {
        const tempObj = JSON.parse(val.displayInfo.proActUrl)
        params.title = tempObj.mTitle
        params.storeId = tempObj.mStoreId
      } catch (error) {
        console.log(error)
      }
      this.$refs.collectOrder.open(params)
    },
    handleNumChange(count, wareList) {
      handleChangeCartNumber(count, this.addCartCount, this.baseInfo)
      this.addCartCount = count
      this.cartList = wareList
    },
    handleCopyUrl: debounce(function () {
      const urlParams = {
        utm_source: "Share",
        utm_medium: getTenantId() === 0 ? "mp_web" : "wellcome_web",
        utm_campaign: "PDP"
      }
      const url = appendUrlParams(window.location.href, urlParams)
      this.$copyText(url).then(res => {
        this.$message({ message: this.$t("common.shareWithCopyText"), type: "success", duration: 1000 })
      })
    }, 500),
    singleGiftOrRedeemItem(item) {
      let result
      const optTradeSkus = (item.tradeGoods && item.tradeGoods.optTradeSkus) || []
      const optGiftSkus = (item.giftGoods && item.giftGoods.optGiftSkus) || []

      // 不判断类型，只判断这两个个数相加等于1的时候才显示(保持跟app一样的逻辑处理)
      if (optTradeSkus.length + optGiftSkus.length === 1) {
        if (optTradeSkus.length) {
          result = optTradeSkus[0]
          if (result) {
            result._type = "trade"
          }
        } else if (optGiftSkus.length) {
          result = optGiftSkus[0]
          if (result) {
            result._type = "gift"
          }
        }
      }

      return result
    },
    soldOut(item) {
      const target = this.singleGiftOrRedeemItem(item)
      if (!target) {
        return false
      }

      return target.skuTradeStatus != 8
    },
    multipleGifts(item) {
      return item.giftGoods && item.giftGoods.autoGift
    },
    multipleGiftsOrRedeemItemList(item) {
      const optTradeSkus = (item.tradeGoods && item.tradeGoods.optTradeSkus) || []
      const optGiftSkus = (item.giftGoods && item.giftGoods.optGiftSkus) || []
      optTradeSkus.forEach(item => (item._type = "trade"))
      optGiftSkus.forEach(item => (item._type = "gift"))
      return [...optTradeSkus, ...optGiftSkus]
    },
    multipleSoldOut(item) {
      return item.skuTradeStatus != 8
    },
    giftClick(item) {
      this.$navigation.push(
        `${this.MAIN_ORIGIN}${this.MAIN_PATH}/productDetails?skuId=${item.skuId}&name=${encodeURIComponent(
          item.skuName
        )}`
      )
    },
    displayInfoProInfo(item) {
      let displayInfo = ""
      const code = this.lang == "en" ? ", " : "；"
      displayInfo = item.displayInfo.proSlogan ? item.displayInfo.proSlogan : ""
      displayInfo = item.displayInfo.proLimitDesc ? displayInfo + code + item.displayInfo.proLimitDesc : displayInfo
      displayInfo = item.displayInfo.disclaimerDesc ? displayInfo + code + item.displayInfo.disclaimerDesc : displayInfo
      return displayInfo
    }
  }
}
